import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import InstagramBottomNav from './InstagramBottomNav';
import eyeLogo from '../assets/relationprotector-eye.svg';

const NAV_ITEMS = [
  { path: '/dashboard', icon: '🏠', label: 'Dashboard' },
  { path: '/dashboard/location', icon: '📍', label: 'Live Location' },
  { path: '/dashboard/chat', icon: '💬', label: 'Chat' },
  { path: '/dashboard/analytics', icon: '📊', label: 'Analytics' },
  { path: '/dashboard/control', icon: '🎮', label: 'Device Control' },
  { path: '/dashboard/sos', icon: '🆘', label: 'SOS Alerts', alertKey: 'sos' },
  { path: '/dashboard/logs', icon: '📋', label: 'Access Logs' },
  { path: '/dashboard/settings', icon: '⚙️', label: 'Settings' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { isConnected, sosAlerts } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pageTitles = {
    '/dashboard': 'Dashboard',
    '/dashboard/location': 'Live Location',
    '/dashboard/chat': 'Chat',
    '/dashboard/analytics': 'Usage Analytics',
    '/dashboard/control': 'Device Control',
    '/dashboard/sos': 'SOS Alerts',
    '/dashboard/logs': 'Access Logs',
    '/dashboard/settings': 'Settings',
  };

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon"><img src={eyeLogo} alt="RelationProtector" className="brand-logo-img" /></div>
          <div className="sidebar-logo-text">
            <h2>RelationProtector</h2>
            <span>Parent Dashboard</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <span className="nav-section-label">Navigation</span>
          {NAV_ITEMS.map(item => {
            const isActive = location.pathname === item.path;
            const badgeCount = item.alertKey === 'sos' ? sosAlerts.length : 0;
            return (
              <button
                key={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
                {badgeCount > 0 && <span className="nav-badge">{badgeCount}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">
              {user?.name?.[0]?.toUpperCase() || 'P'}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">Parent Account</div>
            </div>
            <button
              className="btn btn-ghost btn-sm btn-icon"
              onClick={handleLogout}
              title="Logout"
            >🚪</button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="topbar">
          <div className="topbar-title">
            Relation<span>Protector</span> — {pageTitles[location.pathname] || 'Dashboard'}
          </div>
          <div className="topbar-actions">
            <div className={`connection-badge ${isConnected ? '' : 'disconnected'}`}>
              <span className="connection-dot" />
              {isConnected ? 'Live' : 'Disconnected'}
            </div>
            {sosAlerts.length > 0 && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => navigate('/dashboard/sos')}
              >
                🆘 {sosAlerts.length} SOS
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setSidebarOpen(o => !o)}
              style={{ display: 'none' }}
            >☰</button>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {/* Instagram Bottom Navigation - Mobile */}
      <InstagramBottomNav />
    </div>
  );
}
